import { CommonModule } from "@angular/common";
import { Component, DestroyRef, inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { Router } from "@angular/router";
import { NxErrorModule } from "@aposin/ng-aquila/base";
import { NxButtonModule } from "@aposin/ng-aquila/button";
import { NxFormfieldModule } from "@aposin/ng-aquila/formfield";
import { NxGridModule } from "@aposin/ng-aquila/grid";
import { NxIconModule } from "@aposin/ng-aquila/icon";
import { NxInputModule } from "@aposin/ng-aquila/input";
import { NxLinkModule } from "@aposin/ng-aquila/link";
import { NxMessageModule } from "@aposin/ng-aquila/message";
import { CommonModalComponent } from "@modules/modals/common-modal/common-modal.component";
import { TranslateModule } from "@ngx-translate/core";
import { Select, Store } from "@ngxs/store";
import { CrossAppNavigationService } from "@services/cross-app-navigation/cross-app-navigation.service";
import { CustomValidators } from "@shared/classes/custom-validator";
import { changePasswordLeaveModal } from "@shared/data/modalData";
import { NxPasswordTouchToggleDirective } from "@shared/directives/nx-password-touch-toggle.directive";
import { debounceTime } from "rxjs/operators";
import { ModalService } from "src/app/shared/services/modal.service";
import { CHANGE_PASSWORD } from "src/app/store/profile/profile.action";
import { ProfileState } from "src/app/store/profile/profile.state";
import { GET_SECRET_QUESTION, LOGOUT } from "src/app/store/user/user.action";
import { UserState } from "src/app/store/user/user.state";
import { environment } from "src/environments/environment";

import { AuthSideHeaderComponent } from "../auth-side-header/auth-side-header.component";

@Component({
  selector: "app-password-expired",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NxButtonModule,
    NxErrorModule,
    NxFormfieldModule,
    NxGridModule,
    NxIconModule,
    NxInputModule,
    NxLinkModule,
    NxMessageModule,
    TranslateModule,
    AuthSideHeaderComponent,
    NxPasswordTouchToggleDirective,
  ],
  templateUrl: "./password-expired.component.html",
  styleUrl: "./password-expired.component.scss",
})
export class PasswordExpiredComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  @Select(UserState.secretQuestionDescp) protected secretQuestionDescp$;
  @Select(ProfileState.getchangePasswordState) protected changePasswordState$;

  protected profileChangePasswordForm!: FormGroup;
  protected changePasswordErrorMsg: string = "";
  protected changePasswordErrorTitle: string = "";
  protected formDirty: boolean = false;
  protected success: boolean = false;
  protected backendError: boolean = false;

  protected get newPasswordErrorType() {
    const errors =
      this.profileChangePasswordForm.controls["newPassword"].errors;
    return errors ? this.getErrorType(errors) : null;
  }

  protected get confirmNewPasswordErrorType() {
    const errors =
      this.profileChangePasswordForm.controls["confirmNewPassword"].errors;
    return errors ? this.getErrorType(errors) : null;
  }

  constructor(
    private readonly fb: FormBuilder,
    private readonly store: Store,
    private readonly router: Router,
    private readonly modalService: ModalService,
    private readonly crossAppNavigationService: CrossAppNavigationService,
  ) {}

  ngOnInit() {
    this.getSecretQuestion();
    this.formInit();
    this.onChangeSecretAnswer();
    this.onChangeCurrentPassword();
    this.onChangeNewPassword();
    this.onChangeConfirmNewPassword();
    window.scroll(0, 0);
  }

  private getSecretQuestion() {
    const user = this.store.selectSnapshot<any>(
      (state) => state.UserState.user,
    );
    this.store.dispatch(new GET_SECRET_QUESTION(user.userId));
  }

  private formInit() {
    this.profileChangePasswordForm = this.fb.group({
      secretAnswer: ["", Validators.required],
      currentPassword: ["", Validators.required],
      newPassword: [
        "",
        Validators.compose([
          Validators.required,
          CustomValidators.patternValidator(/^.{15,20}$/, {
            minAndMaxNum: true,
          }),
          CustomValidators.patternValidator(/^(?=.*[a-zA-Z])(?=.*[0-9])/, {
            hasAlphanumeric: true,
          }),
          CustomValidators.patternValidator(/^(?=.*[a-z])(?=.*[A-Z])/, {
            uppercaseAndLowercase: true,
          }),
          CustomValidators.patternValidator(/[@#$^%&*!]/, {
            hasSpecialCharacters: true,
          }),
        ]),
      ],
      confirmNewPassword: ["", Validators.required],
    });
  }

  protected onSubmit() {
    if (
      this.profileChangePasswordForm.controls["newPassword"].value !==
      this.profileChangePasswordForm.controls["confirmNewPassword"].value
    ) {
      this.profileChangePasswordForm.controls["confirmNewPassword"].setErrors({
        passwordMisMatch: true,
      });
      return;
    }

    const user = this.store.selectSnapshot<any>(
      (state) => state.UserState.user,
    );
    const payload = {
      userId: user.userId,
      secretAnswer:
        this.profileChangePasswordForm.controls["secretAnswer"].value,
      currentPassword:
        this.profileChangePasswordForm.controls["currentPassword"].value,
      newPassword: this.profileChangePasswordForm.controls["newPassword"].value,
      confirmedNewPassword:
        this.profileChangePasswordForm.controls["confirmNewPassword"].value,
    };

    this.store
      .dispatch(new CHANGE_PASSWORD(payload))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        const changePasswordState = res.ProfileState.changePasswordState;
        if (changePasswordState.isChangePasswordSuccess) {
          this.success = true;
          this.backendError = false;
          this.changePasswordErrorMsg = "";
          this.changePasswordErrorTitle = "";
          localStorage.setItem("loginSource", "sat");

          if (environment.mockServerApiEnabled) {
            this.router.navigate(["/summary"]);
            return;
          }

          this.crossAppNavigationService.redirectToAlphaSatPath(
            "/home?token=TOKEN",
          );
          return;
        }

        this.backendError = true;
        this.success = false;
        this.changePasswordErrorMsg =
          changePasswordState.changePasswordErrorMsg;
        this.changePasswordErrorTitle =
          changePasswordState.changePasswordErrorTitle;
      });
  }

  protected toLeave() {
    if (this.formDirty && !this.success) {
      this.modalService.openDialogWithCallbackHandler(
        CommonModalComponent,
        changePasswordLeaveModal,
        (result) => {
          if (result) {
            this.store.dispatch(new LOGOUT());
          }
        },
      );
      return;
    }

    this.store.dispatch(new LOGOUT());
  }

  private onChangeSecretAnswer() {
    this.profileChangePasswordForm
      .get("secretAnswer")
      .valueChanges.pipe(debounceTime(250), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.formDirty = this.profileChangePasswordForm.dirty;
        this.backendError = false;
      });
  }

  private onChangeCurrentPassword() {
    this.profileChangePasswordForm
      .get("currentPassword")
      .valueChanges.pipe(debounceTime(250), takeUntilDestroyed(this.destroyRef))
      .subscribe((currentPassword: string) => {
        const newPassword =
          this.profileChangePasswordForm.controls["newPassword"].value;
        this.formDirty = this.profileChangePasswordForm.dirty;
        this.backendError = false;
        this.profileChangePasswordForm.controls[
          "newPassword"
        ].updateValueAndValidity();
        if (newPassword && currentPassword) {
          this.checkPasswordMatch(currentPassword, newPassword);
        }
      });
  }

  private onChangeNewPassword() {
    this.profileChangePasswordForm
      .get("newPassword")
      .valueChanges.pipe(debounceTime(250), takeUntilDestroyed(this.destroyRef))
      .subscribe((newPassword: string) => {
        const currentPassword =
          this.profileChangePasswordForm.controls["currentPassword"].value;
        this.formDirty = this.profileChangePasswordForm.dirty;
        this.backendError = false;
        this.resetConfirmNewPassword();
        if (newPassword && currentPassword) {
          this.checkPasswordMatch(currentPassword, newPassword);
        }
      });
  }

  private onChangeConfirmNewPassword() {
    this.profileChangePasswordForm
      .get("confirmNewPassword")
      .valueChanges.pipe(debounceTime(250), takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmNewPassword: string) => {
        const newPassword =
          this.profileChangePasswordForm.controls["newPassword"].value;
        this.formDirty = this.profileChangePasswordForm.dirty;
        this.backendError = false;
        if (newPassword && confirmNewPassword) {
          this.checkPasswordMisMatch(newPassword, confirmNewPassword);
        }
      });
  }

  private resetConfirmNewPassword() {
    this.profileChangePasswordForm.patchValue({
      confirmNewPassword: "",
    });
    this.profileChangePasswordForm.controls["confirmNewPassword"].setErrors(
      null,
    );
  }

  private checkPasswordMatch(currentPassword: string, newPassword: string) {
    if (currentPassword === newPassword) {
      this.profileChangePasswordForm.controls["newPassword"].setErrors({
        samePassword: true,
      });
    }
  }

  private checkPasswordMisMatch(
    newPassword: string,
    confirmNewPassword: string,
  ) {
    if (newPassword !== confirmNewPassword) {
      this.profileChangePasswordForm.controls["confirmNewPassword"].setErrors({
        passwordMisMatch: true,
      });
    }
  }

  private getErrorType(errors) {
    if (errors.passwordMisMatch) {
      return "passwordMisMatch";
    }
    if (errors.samePassword) {
      return "samePassword";
    }
    if (errors.required) {
      return "required";
    }
    return null;
  }
}
